import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import moment from "moment";
import Swal from "sweetalert2";
import { MdRefresh, MdOutlineFileUpload } from "react-icons/md";

import Container from "../../components/Container";
import Loading from "../../../home/components/Loading";
import { BrComerceApiService } from "../../../../services/BrComerceApiService";
import CepSearch from "./CepSearch";
import styles from "./styles.module.scss";

const statusInfo = {
  pending: { label: "Aguardando", className: "badgePending" },
  processing: { label: "Processando", className: "badgeProcessing" },
  done: { label: "Concluído", className: "badgeDone" },
  error: { label: "Erro", className: "badgeError" }
};

const formatDate = date =>
  date ? moment(date).format("DD/MM/YYYY HH:mm") : "-";

const totalCeps = counts => {
  if (!counts) return "-";
  const total = Object.values(counts).reduce((acc, n) => acc + (Number(n) || 0), 0);
  return total.toLocaleString("pt-BR");
};

const DneBasePage = () => {
  window.setPageTitle("Base de Endereços (DNE)");

  const api = new BrComerceApiService();

  const [loading, setLoading] = useState(true);
  const [imports, setImports] = useState([]);

  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const fileInputRef = useRef(null);

  const loadImports = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.makeHttpRequest({
        method: "GET",
        url: "/dne/imports"
      });
      setImports(response?.data || response || []);
    } catch (e) {
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: "Houve um erro ao carregar o histórico de importações."
      });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadImports();
  }, [loadImports]);

  // Enquanto houver carga em andamento, atualiza o historico sozinho.
  useEffect(() => {
    const hasRunning = imports.some(i =>
      ["pending", "processing"].includes(i.status)
    );
    if (!hasRunning) return undefined;

    const interval = setInterval(async () => {
      try {
        const response = await api.makeHttpRequest({
          method: "GET",
          url: "/dne/imports"
        });
        setImports(response?.data || response || []);
      } catch (e) {
        // silencioso: proxima rodada tenta de novo
      }
    }, 20000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imports]);

  const onFileChange = e => {
    const selected = e.target.files[0];
    if (selected && !selected.name.toLowerCase().endsWith(".zip")) {
      Swal.fire({
        icon: "warning",
        title: "Atenção",
        text: "Envie o zip do eDNE exatamente como baixado dos Correios (.zip)."
      });
      e.target.value = "";
      return;
    }
    setFile(selected || null);
  };

  const uploadFile = async () => {
    if (!file) return;

    setUploading(true);
    setProgress(0);

    try {
      const response = await api.makeHttpRequest({
        method: "POST",
        url: "/dne/upload-url",
        data: { filename: file.name }
      });
      const { url } = response?.data || response;

      // PUT direto no S3 com a presigned URL (sem Authorization: a URL ja
      // carrega a assinatura; o header quebraria a validacao do S3).
      await axios.put(url, file, {
        headers: { "Content-Type": file.type || "application/zip" },
        onUploadProgress: e => {
          if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
        }
      });

      Swal.fire({
        icon: "success",
        title: "Base enviada!",
        text:
          "O processamento foi iniciado automaticamente e aparece no histórico abaixo em instantes."
      });

      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";

      // Da tempo do evento S3 acionar a Lambda e o registro ser criado.
      setTimeout(loadImports, 5000);
    } catch (e) {
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: "Falha ao enviar a base. Tente novamente."
      });
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  return (
    <>
      <Loading isLoading={loading && !imports.length} />

      <Container title="Base de Endereços (DNE)">
        <div className={styles.contentBody}>
          <CepSearch />

          <div className={styles.uploadCard}>
            <div className={styles.uploadInfo}>
              <h5>Atualização da base eDNE dos Correios</h5>
              <p>
                Envie o arquivo <strong>eDNE_Basico.zip</strong> exatamente como
                baixado dos Correios. O upload vai direto para o S3 e a
                importação inicia sozinha — acompanhe o status na tabela abaixo.
              </p>
            </div>

            <div className={styles.uploadActions}>
              <label className={styles.fileLabel}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".zip"
                  onChange={onFileChange}
                  disabled={uploading}
                />
                <span>{file ? file.name : "Selecionar arquivo (.zip)"}</span>
              </label>

              <button
                type="button"
                className={styles.btnUpload}
                disabled={!file || uploading}
                onClick={uploadFile}
              >
                <MdOutlineFileUpload />
                <span>{uploading ? `Enviando... ${progress}%` : "Enviar base"}</span>
              </button>
            </div>

            {uploading && (
              <div className={styles.progressBar}>
                <div style={{ width: `${progress}%` }} />
              </div>
            )}
          </div>

          <div className={styles.tableHeader}>
            <h5>Histórico de importações</h5>
            <button
              type="button"
              className={styles.btnRefresh}
              onClick={loadImports}
              title="Atualizar"
            >
              <MdRefresh size={20} />
              <span>Atualizar</span>
            </button>
          </div>

          <div className={styles.contentTable}>
            <table className={styles.tableContainer}>
              <thead>
                <tr>
                  <th width="30%">Arquivo</th>
                  <th width="8%">Versão</th>
                  <th width="12%">Status</th>
                  <th width="12%">CEPs carregados</th>
                  <th width="14%">Início</th>
                  <th width="14%">Conclusão</th>
                  <th width="10%">Detalhes</th>
                </tr>
              </thead>
              <tbody>
                {imports.length > 0
                  ? imports.map(item => {
                      const info = statusInfo[item.status] || {
                        label: item.status,
                        className: "badgePending"
                      };
                      return (
                        <tr key={item.id}>
                          <td className={styles.filenameCell} title={item.filename}>
                            {item.filename}
                          </td>
                          <td>{item.version || "-"}</td>
                          <td>
                            <span className={`${styles.badge} ${styles[info.className]}`}>
                              {info.label}
                            </span>
                          </td>
                          <td>{totalCeps(item.counts)}</td>
                          <td>{formatDate(item.started_at)}</td>
                          <td>{formatDate(item.finished_at)}</td>
                          <td className={styles.errorCell} title={item.error_message || ""}>
                            {item.error_message || "-"}
                          </td>
                        </tr>
                      );
                    })
                  : !loading && (
                      <tr>
                        <td
                          colSpan="7"
                          style={{
                            textAlign: "center",
                            padding: "30px",
                            color: "#807e80"
                          }}
                        >
                          Nenhuma importação realizada ainda. Envie a primeira
                          base acima.
                        </td>
                      </tr>
                    )}
              </tbody>
            </table>
          </div>
        </div>
      </Container>
    </>
  );
};

export default DneBasePage;
